import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Eye, GripVertical } from 'lucide-react';

import ProfileHeader from '../components/ProfileHeader';
import { PresentationSection, ExperienceSection, EducationSection } from '../components/ProfileSections';
import ProfileSidebar from '../components/ProfileSidebar';
import ProfileWall from '../components/ProfileWall';
import { TipModal, ReviewsModal } from '../components/ProfileModals';
import FollowListModal from '../components/FollowListModal';
import ShareModal from '../components/ShareModal';
import SharePanel from '../components/SharePanel';
import ProfileEditModal from '../components/ProfileEditModal';
import PublicExpertCalendar from '../components/PublicExpertCalendar';
import CreateEventModal from '../components/CreateEventModal';
import Breadcrumbs from '../components/Breadcrumbs';
import { ProfileSkeleton } from '../components/SkeletonLoader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const resolveUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('/')) return `${BACKEND_URL}${url}`;
  if (url.includes('preview.emergentagent.com/api/uploads')) {
    const match = url.match(/\/api\/uploads\/.+/);
    if (match) return `${BACKEND_URL}${match[0]}`;
  }
  return url;
};

const DEFAULT_SECTION_ORDER = ['presentation', 'experience', 'education', 'calendar'];

const SortableSection = ({ id, children, isOwner }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1, position: 'relative' };
  return (
    <div ref={setNodeRef} style={style} className="group/section">
      {isOwner && (
        <button {...attributes} {...listeners} className="absolute -left-3 top-4 z-10 p-1.5 bg-white dark:bg-gray-700 rounded-lg shadow-md border border-gray-200 dark:border-gray-600 opacity-0 group-hover/section:opacity-100 transition-opacity cursor-grab active:cursor-grabbing" title="Glisser pour reorganiser" data-testid={`drag-handle-${id}`}>
          <GripVertical className="h-4 w-4 text-gray-400" />
        </button>
      )}
      {children}
    </div>
  );
};

const Profile = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user: currentUser, refreshUser } = useAuth();

  const [profile, setProfile] = useState(null);
  const [communities, setCommunities] = useState([]);
  const [products, setProducts] = useState([]);
  const [badges, setBadges] = useState([]);
  const [sessionTypes, setSessionTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [followLoading, setFollowLoading] = useState(false);

  // Modal states
  const [showFollowModal, setShowFollowModal] = useState(null);
  const [showTipModal, setShowTipModal] = useState(false);
  const [tipAmount, setTipAmount] = useState('');
  const [tipLoading, setTipLoading] = useState(false);
  const [tipStatus, setTipStatus] = useState(null);
  const [showReviewsModal, setShowReviewsModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateEventModal, setShowCreateEventModal] = useState(false);

  // Expandable sections
  const [showAllExperiences, setShowAllExperiences] = useState(false);
  const [showAllEducation, setShowAllEducation] = useState(false);

  // Rating visibility
  const [ratingVisibility, setRatingVisibility] = useState(true);
  const [ratingLoading, setRatingLoading] = useState(false);

  // Section drag-and-drop
  const [sectionOrder, setSectionOrder] = useState(DEFAULT_SECTION_ORDER);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const viewAsVisitor = searchParams.get('view') === 'visitor';
  const editTarget = searchParams.get('edit');

  // Auto-open edit modal when ?edit=... is present (Q4 contextual CTA from Home)
  const isOwner = currentUser?.user_id === userId;
  useEffect(() => {
    if (editTarget && isOwner) {
      setShowEditModal(true);
    }
  }, [editTarget, isOwner]);

  // ── Data Loading ────────────────────────────────────────────────────────
  useEffect(() => { loadProfile(); loadUserCommunities(); loadUserProducts(); loadUserBadges(); loadSessionTypes(); }, [userId]);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const tip = searchParams.get('tip');
    if (sessionId && tip === 'success') pollTipStatus(sessionId);
    else if (tip === 'cancelled') { setTipStatus({ status: 'cancelled', message: 'Tip annule' }); setTimeout(() => setTipStatus(null), 5000); }
  }, [searchParams]);

  // Brief 27 oct B.3 — CTA « Suivre » depuis la page publique : ?follow=1
  // déclenche le suivi une fois le profil chargé (visiteur passé par signup/login).
  const autoFollowDone = React.useRef(false);
  useEffect(() => {
    if (searchParams.get('follow') !== '1' || !profile || isOwner || autoFollowDone.current) return;
    autoFollowDone.current = true;
    if (!profile.is_following) handleFollow();
    window.history.replaceState({}, '', `/app/profile/${userId}`);
  }, [searchParams, profile, isOwner]); // eslint-disable-line react-hooks/exhaustive-deps

  // OG meta tags
  useEffect(() => {
    if (!profile) return;
    document.title = `${profile.display_name || 'Profil'} | OpenSapiens`;
    const desc = profile.bio ? profile.bio.substring(0, 160) : `Decouvrez ${profile.display_name || 'ce profil'} sur OpenSapiens`;
    const setMeta = (prop, content, isName = false) => {
      const attr = isName ? 'name' : 'property';
      let meta = document.querySelector(`meta[${attr}="${prop}"]`);
      if (!meta) { meta = document.createElement('meta'); meta.setAttribute(attr, prop); document.head.appendChild(meta); }
      meta.setAttribute('content', content);
    };
    setMeta('og:type', 'profile'); setMeta('og:title', document.title); setMeta('og:description', desc);
    setMeta('og:image', profile.avatar || `${BACKEND_URL}/og-image-default.png`);
    setMeta('description', desc, true);
  }, [profile, userId]);

  const loadProfile = async () => {
    if (!userId || userId.length < 8) { setLoading(false); return; }
    try {
      const response = await axios.get(`${API}/users/${userId}`, { withCredentials: true });
      setProfile(response.data);
      setEditData(response.data);
      setRatingVisibility(response.data.show_rating !== false);
      if (response.data.section_order) setSectionOrder(response.data.section_order);
    } catch (error) { console.error('Error loading profile:', error); }
    finally { setLoading(false); }
  };

  const loadUserCommunities = async () => {
    if (!userId || userId.length < 8) return;
    try { const r = await axios.get(`${API}/users/${userId}/communities`, { withCredentials: true }); setCommunities(r.data.communities); }
    catch (e) { console.error('Error:', e); }
  };
  const loadUserProducts = async () => {
    if (!userId || userId.length < 8) return;
    try { const r = await axios.get(`${API}/users/${userId}/products`, { withCredentials: true }); setProducts((r.data.products || []).sort((a, b) => (b.like_count || 0) - (a.like_count || 0)).slice(0, 3)); }
    catch (e) { console.error('Error:', e); }
  };
  const loadUserBadges = async () => {
    if (!userId || userId.length < 8) return;  // defensive : skip if userId malformed
    try { const r = await axios.get(`${API}/users/${userId}/badges`, { withCredentials: true }); setBadges(r.data.badges?.map(b => b.id) || []); }
    catch (e) { console.error('Error:', e); }
  };
  const loadSessionTypes = async () => {
    if (!userId || userId.length < 8) return;  // defensive : skip if userId malformed
    try { const r = await axios.get(`${API}/bookings/users/${userId}/session-types`, { withCredentials: true }); setSessionTypes(r.data.session_types || []); }
    catch (e) { console.error('Error:', e); }
  };

  // ── Actions ─────────────────────────────────────────────────────────────
  const handleFollow = async () => {
    setFollowLoading(true);
    try {
      if (profile.is_following) await axios.delete(`${API}/users/${userId}/follow`, { withCredentials: true });
      else await axios.post(`${API}/users/${userId}/follow`, {}, { withCredentials: true });
      loadProfile();
    } catch (e) { console.error('Error:', e); }
    finally { setFollowLoading(false); }
  };

  const pollTipStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 5) { setTipStatus({ status: 'pending', message: 'Verification en cours...' }); return; }
    try {
      const r = await axios.get(`${API}/payments/status/${sessionId}`, { withCredentials: true });
      if (r.data.payment_status === 'paid') { setTipStatus({ status: 'success', message: 'Merci pour votre tip !' }); window.history.replaceState({}, '', `/app/profile/${userId}`); return; }
      setTimeout(() => pollTipStatus(sessionId, attempts + 1), 2000);
    } catch (e) { console.error('Error:', e); }
  };

  const handleSendTip = async () => {
    const amount = parseFloat(tipAmount);
    if (!amount || amount < 1) { setTipStatus({ status: 'error', message: 'Montant minimum: 1 CHF' }); return; }
    setTipLoading(true);
    try {
      const r = await axios.post(`${API}/payments/checkout/tip`, { recipient_id: userId, amount, origin_url: window.location.origin }, { withCredentials: true });
      if (r.data.checkout_url) window.location.href = r.data.checkout_url;
    } catch (e) { setTipStatus({ status: 'error', message: e.response?.data?.detail || 'Erreur lors du paiement' }); }
    finally { setTipLoading(false); }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const newOrder = arrayMove(sectionOrder, sectionOrder.indexOf(active.id), sectionOrder.indexOf(over.id));
    setSectionOrder(newOrder);
    try { await axios.put(`${API}/users/me`, { section_order: newOrder }, { withCredentials: true }); } catch (e) { console.error('Error:', e); }
  };

  const toggleRatingVisibility = async () => {
    setRatingLoading(true);
    try { await axios.put(`${API}/users/me/rating-visibility`, { show_rating: !ratingVisibility }, { withCredentials: true }); setRatingVisibility(!ratingVisibility); }
    catch (e) { console.error('Error:', e); }
    finally { setRatingLoading(false); }
  };

  // Edit helpers
  const addExperience = () => setEditData({ ...editData, experience: [...(editData.experience || []), { title: '', company: '', years: '', description: '' }] });
  const removeExperience = (i) => setEditData({ ...editData, experience: editData.experience.filter((_, j) => j !== i) });
  const updateExperience = (i, field, val) => { const n = [...editData.experience]; n[i] = { ...n[i], [field]: val }; setEditData({ ...editData, experience: n }); };
  const addEducation = () => setEditData({ ...editData, education: [...(editData.education || []), { degree: '', school: '', year: '', description: '' }] });
  const removeEducation = (i) => setEditData({ ...editData, education: editData.education.filter((_, j) => j !== i) });
  const updateEducation = (i, field, val) => { const n = [...editData.education]; n[i] = { ...n[i], [field]: val }; setEditData({ ...editData, education: n }); };
  const addSkill = () => { const s = prompt('Ajouter une competence:'); if (s) setEditData({ ...editData, skills: [...(editData.skills || []), s] }); };
  const removeSkill = (i) => setEditData({ ...editData, skills: editData.skills.filter((_, j) => j !== i) });

  // ── Render ──────────────────────────────────────────────────────────────
  if (loading) return <ProfileSkeleton />;
  if (!profile) return <div className="text-center py-20"><p className="text-gray-500">Profil non trouve</p></div>;

  const bannerStyle = profile.banner_url ? {
    backgroundImage: `url(${resolveUrl(profile.banner_url)})`, backgroundSize: 'cover', backgroundPosition: 'center'
  } : profile.banner_gradient ? {
    background: `linear-gradient(${profile.banner_gradient.direction || 'to right'}, ${profile.banner_gradient.color1}, ${profile.banner_gradient.color2})`
  } : { background: 'linear-gradient(to right, #667eea, #764ba2)' };

  const showAsOwner = profile.is_own_profile && !viewAsVisitor;

  return (
    <div className="max-w-4xl mx-auto" data-testid="profile-page">
      <Breadcrumbs customItems={[
        { name: 'Decouvrir', path: '/app/discover', isLast: false },
        { name: profile.display_name || 'Profil', path: location.pathname, isLast: true }
      ]} />

      {/* Visitor Preview Banner */}
      {viewAsVisitor && profile.is_own_profile && (
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-3 rounded-xl mb-4 flex items-center justify-between shadow-lg">
          <div className="flex items-center gap-3">
            <Eye className="h-5 w-5" />
            <div>
              <p className="font-medium">Mode apercu visiteur</p>
              <p className="text-sm text-white/80">Vous voyez votre profil tel qu'il apparait aux autres utilisateurs</p>
            </div>
          </div>
          <Button size="sm" variant="secondary" className="bg-white text-orange-600 hover:bg-orange-50" onClick={() => navigate(`/app/profile/${userId}`)}>
            Quitter l'apercu
          </Button>
        </div>
      )}

      {/* Header */}
      <ProfileHeader
        profile={profile} userId={userId} badges={badges} showAsOwner={showAsOwner}
        isEditing={isEditing} editData={editData} setEditData={setEditData}
        bannerStyle={bannerStyle} onFollow={handleFollow} followLoading={followLoading}
        onShowFollowModal={setShowFollowModal} onShowTipModal={setShowTipModal}
        onShowShareModal={setShowShareModal} onShowEditModal={setShowEditModal}
        onShowReviewsModal={setShowReviewsModal}
      />

      {/* Brief B5 P1 §6 — Présentation pleine largeur (2 colonnes) sur profil
          public ET owner. Sortie de la grille principale pour s'étirer sous
          le hero, les autres sections gardent le layout 2/3 + sidebar. */}
      {sectionOrder.includes('presentation') && (
        <div className="mb-6" data-testid="section-presentation-full-width">
          <SortableSection key="presentation" id="presentation" isOwner={showAsOwner}>
            <PresentationSection profile={profile} isEditing={isEditing} editData={editData} setEditData={setEditData} />
          </SortableSection>
        </div>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={sectionOrder} strategy={verticalListSortingStrategy}>
              {sectionOrder.map(sectionId => {
                // Brief B5 P1 §6 — `presentation` rendue hors grille (pleine largeur)
                if (sectionId === 'presentation') return null;
                if (sectionId === 'experience') return (
                  <SortableSection key="experience" id="experience" isOwner={showAsOwner}>
                    <ExperienceSection profile={profile} isEditing={isEditing} editData={editData}
                      showAll={showAllExperiences} setShowAll={setShowAllExperiences}
                      onAdd={addExperience} onRemove={removeExperience} onUpdate={updateExperience} />
                  </SortableSection>
                );
                if (sectionId === 'education') return (
                  <SortableSection key="education" id="education" isOwner={showAsOwner}>
                    <EducationSection profile={profile} isEditing={isEditing} editData={editData}
                      showAll={showAllEducation} setShowAll={setShowAllEducation}
                      onAdd={addEducation} onRemove={removeEducation} onUpdate={updateEducation} />
                  </SortableSection>
                );
                if (sectionId === 'calendar') return (
                  <SortableSection key="calendar" id="calendar" isOwner={showAsOwner}>
                    <div data-testid="section-calendar">
                      <PublicExpertCalendar userId={userId} isOwnProfile={showAsOwner} />
                    </div>
                  </SortableSection>
                );
                return null;
              })}
            </SortableContext>
          </DndContext>

          {/* ── Visual Separator: Profile Info → Wall ── */}
          <div className="relative mt-10 mb-6" data-testid="profile-wall-divider">
            <div className="absolute inset-0 flex items-center" aria-hidden="true">
              <div className="w-full border-t-2 border-dashed border-purple-200 dark:border-purple-800/50" />
            </div>
            <div className="relative flex justify-center">
              <span className="bg-gray-50 dark:bg-gray-900 px-5 py-2 rounded-full flex items-center gap-2.5 shadow-sm border border-purple-100 dark:border-purple-800/40">
                <svg className="h-4.5 w-4.5 text-purple-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 6h16M4 10h16M4 14h10M4 18h7"/></svg>
                <span className="text-sm font-semibold text-purple-700 dark:text-purple-300 tracking-wide uppercase">Publications</span>
                <span className="text-xs text-gray-400 dark:text-gray-500 font-normal">Mur</span>
              </span>
            </div>
          </div>

          {/* Brief Jul 3 2026 — Section Paiements RETIRÉE du profil :
              l'entrée d'onboarding Stripe est désormais dans la colonne
              Transmettre de /app/home. Le bouton "Gérer mon compte Stripe" est
              accessible depuis /app/admin (dashboard admin). */}

          {/* Profile Wall - User's posts */}
          <div data-testid="profile-wall-section">
            <ProfileWall userId={userId} isOwnProfile={showAsOwner} />
          </div>
        </div>

        {/* Sidebar */}
        <ProfileSidebar
          profile={profile} userId={userId} showAsOwner={showAsOwner}
          isEditing={isEditing} editData={editData} setEditData={setEditData}
          products={products} communities={communities} badges={badges}
          sessionTypes={sessionTypes}
          onAddSkill={addSkill} onRemoveSkill={removeSkill}
        />
      </div>

      {/* Modals */}
      <FollowListModal isOpen={showFollowModal !== null} onClose={() => setShowFollowModal(null)} userId={userId} type={showFollowModal} title={showFollowModal === 'followers' ? 'Abonnes' : 'Abonnements'} />

      {tipStatus && (
        <div className={`fixed bottom-4 right-4 p-4 rounded-xl shadow-lg flex items-center gap-3 z-50 ${
          tipStatus.status === 'success' ? 'bg-green-50 border border-green-200 text-green-700' :
          tipStatus.status === 'error' ? 'bg-red-50 border border-red-200 text-red-700' : 'bg-blue-50 border border-blue-200 text-blue-700'
        }`}>
          <span>{tipStatus.message}</span>
          <button onClick={() => setTipStatus(null)} className="hover:opacity-70">x</button>
        </div>
      )}

      <TipModal show={showTipModal} onClose={() => setShowTipModal(false)} profile={profile} tipAmount={tipAmount} setTipAmount={setTipAmount} onSendTip={handleSendTip} tipLoading={tipLoading} />
      <ReviewsModal show={showReviewsModal} onClose={() => setShowReviewsModal(false)} profile={profile} userId={userId} />
      {showAsOwner && profile?.handle ? (
        <SharePanel
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          title="Partager mon profil"
          shareUrl={`${window.location.origin}/api/@${profile.handle}`}
          prettyUrl={`${window.location.origin}/@${profile.handle}`}
          defaultMessage={'Découvrez mon profil et réservez une session avec moi 👇'}
        />
      ) : (
        /* Brief 27 oct B.1 — profil d'un tiers : copier / WhatsApp / LinkedIn / email / DM interne / communauté */
        <ShareModal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          shareType="profile"
          shareData={{
            id: userId,
            handle: profile?.handle,
            title: profile?.display_name,
            description: profile?.expert_pitch || profile?.bio || `Découvrez ${profile?.display_name} sur OpenSapiens`,
          }}
        />
      )}
      <ProfileEditModal isOpen={showEditModal} onClose={() => setShowEditModal(false)} profile={profile} initialTab={searchParams.get('edit')} onSave={(updatedData) => { setProfile({ ...profile, ...updatedData }); refreshUser(); }} />
      <CreateEventModal isOpen={showCreateEventModal} onClose={() => setShowCreateEventModal(false)} onEventCreated={(event) => console.log('Event created:', event)} sessionTypes={sessionTypes} />
    </div>
  );
};

export default Profile;
